import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-card/paper-card.js';
import '@polymer/iron-icon/iron-icon.js';
import '@cbar/cbar-chip/cbar-chip.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';
import '@cbar/localizemixin/LocalizeMixin.js';

/**
 * `cbar-entity-card` is an element that acts as a container for entity information. It allows for information in regards to many different entities that can exist in a CBAR platform such as events, people, anything with a subject
 * and description. It allows for an overlook into general information linking to such entities. Currently it shows the following information.
 *
 * - Entity title
 * - Entity Description
 * - Entity Grouping (if exists)`
 * - Entity Type (If "icon" param is passed will show on hover of icon)
 * - A slot for any action buttons or custom information wanting to be shown
 * - Entity Image (Hides if NA)
 * - Entity Icon determined by the passed iconType property
 *
 * This card also allows for a slot that allows for the adding and styling of action buttons or any extra information that this element has missed. The slot itself is unnamed to allow for ease of use
 * in order to slot anything in, just put it inside the element declaration.
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarEntityCard extends Cbar.LocalizeMixin(PolymerElement) {
  static get template() {
    return html`
        <style include="cbar-shared-styles">
            :host {
                display: inline-block;
                width: 100%;
                --lh: 1rem;
                line-height: var(--lh);
                --paper-card-header-image: {
                    height: 175px;
                }
            }

            .card-content {
                background-color: var(--cbar-card-background-color, white);
                color: var(--cbar-sys-color-on-surface, black);
                display: flex;
                flex: 1;
                flex-direction: column;
            }

            a {
                color: black;
            }

            paper-button {
                background: transparent;
                padding: 0;
            }

            .interest-name {
                background-color: black;
                opacity: 0.5;
                margin-top: -9%;
                color: var(--cbar-icon-text, white);
                height: 10%;
                text-align: center;
                padding-bottom: 2%;
                padding-top: 2%;
            }

            .description {
                overflow: hidden;
                text-overflow: ellipsis;
                --max-lines: 3;
                max-height: calc(var(--lh) * var(--max-lines));
                display: -webkit-box;
                -webkit-line-clamp: 3; /* number of lines to show */
                -webkit-box-orient: vertical;
            }

            .background-coloured-title {
                display: -webkit-inline-box;
                --max-lines: 2;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                text-overflow: ellipsis;
                overflow: hidden;
            }

            p {
                margin: 0 !important;
            }

            h6 {
                display: inline-block;
                margin-bottom: 8px;
                margin-top: 0;
                color: var(--cbar-sys-color-on-surface, black) !important;
                width: 85%;
                line-height: normal;
            }

            .spacing {
                margin-bottom: 16px;
            }

            paper-card {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
                /* fixes styling issue existing with border-radius */
                padding-bottom: 4px;
            }

            .icon-image-styled > * {
                height: 100px;
                width: 100px;
                margin-top: -135px;
                position: absolute;
                background-color: rgba(255, 255, 255, 0.5);
                margin-left: 15px;
                border-radius: 50%;
                border: 2px solid white;
            }

            .card-actions {
                border-top: 0;
                background-color: var(--cbar-card-background-color, white);
                color: var(--cbar-sys-color-on-surface, black);
            }

            .ending-div {
                height: 30px;
            }

            .top-aligned {
                vertical-align: top;
            }

            .image-div {
                height: 175px;
            }

            .icon-div {
                width: 24px;
                height: 24px;
            }

            iron-icon{
                color: var(--cbar-sys-color-on-surface, black);
            }

            .extra-content {
                height: 30px;
            }

            .header-image {
                display: block;
                width: 100%;
                --iron-image-width: 100%;
                pointer-events: none;
            }

            cbar-chip[data-status='Rejected'] {
                --cbar-chip-background-color: palevioletred;
                --cbar-chip-secondary-text-color: white;
            }

            cbar-chip[data-status='Offered'] {
                --cbar-chip-background-color: #4caf50;
                --cbar-chip-secondary-text-color: white;
            }

            cbar-chip[data-status='Requested'] {
                --cbar-chip-background-color: #FF9800;
                --cbar-chip-secondary-text-color: white;
            }
        </style>
        <template is="dom-if" if="[[!loading]]">
            <paper-card preload-image id="entityCard">

                <template is="dom-if" if="[[__displayImage]]">
                    <iron-image src="[[entityData.image]]" class="header-image"></iron-image>
                </template>

                <template is="dom-if" if="[[__displayDefault]]">
                    <iron-image src="[[defaultImage]]" class="header-image"></iron-image>
                </template>

                <div class="interest-name" id="captionDiv" hidden="">[[entityData.caption]]</div>
                <slot name="head-section">
                    <div class="icon-image-styled" hidden$="[[!entityData.iconImage]]">
                        <iron-image src="[[entityData.iconImage]]" sizing="cover"></iron-image>
                    </div>
                </slot>
                <div class="card-content">
                    <a href="[[defaultUrl]]">
                        <div class="title-div">
                            <h6 class="background-coloured-title">[[entityData.title]]</h6>
                            <iron-icon icon="[[iconType]]" class="right-aligned top-aligned"
                                title="[[entityData.type]]" hidden\$="[[!icon]]"></iron-icon>
                        </div>
                    </a>
                    <template is="dom-if" if="[[entityData.group]]">
                        <div class="spacing body-text-2">[[entityData.group]]</div>
                    </template>

                    <template is="dom-if" if="[[__displayStatus]]">
                        <div class="body-text-2 spacing">
                            <cbar-chip>[[entityData.status]]</cbar-chip>
                        </div>
                    </template>

                    <template is="dom-if" if="[[entityData.description]]">
                        <div class="spacing description body-text-2"></div>
                    </template>
                    <div>
                        <slot></slot>
                    </div>
                </div>
                <div class="card-actions">
                    <slot name="actions"></slot>
                </div>
            </paper-card>
        </template>

        <template is="dom-if" if="[[loading]]">
            <paper-card>
                <div class="image-div loading"></div>
                <div class="card-content">
                    <div class="title-div">
                        <h3 class="background-coloured-title loading"></h3>
                        <div class="right-aligned top-aligned icon-div loading"></div>
                    </div>
                    <div class="extra-content loading"></div>
                </div>
                <div class="card-actions loading">
                </div>
            </paper-card>
        </template>
    `;
  }

  static get is() {
    return 'cbar-entity-card';
  }

  static get properties() {
    return {
      /**
       * Property used for testing, this is private and cannot be called from the outside
       */
      exampleProp: {
        type: String,
        value: 'cbar-entity-card'
      },
      /**
       * Property for getting the entities information, this allows for the displaying of the information
       */
      entityData: {
        type: Object,
        observer: "_handleEntityInformation"
      },
      /**
       * Property that allows for a layout to be placed on the image that describes a tag/interest that shows why the item was shown, as this is not always suitable, we have this set as an option
       */
      interestedVersion: {
        type: Boolean,
        value: false
      },
      /**
       * Property that allows for the icon with the entity type to be shown, if this is not passed, the icon will not show.
       */
      icon: {
        type: Boolean,
        value: false
      },
      /**
       * Property that takes in a value from outside and assigns it to the icon that is required for the entity
       */
      iconType: {
        type: String
      },
      headerClickable: {
        type: Boolean,
        value: false,
        observer: "_setHeaderClickable"
      },
      defaultImage: {
        type: String,
        value: false
      },
      loading: {
        type: Boolean,
        value: false,
        observer: "__checkImage"
      },
      localesFile: {
        type: String,
        value: "/node_modules/@cbar/cbar-localizations/translations.json"
      },
      defaultUrl: {
        type: String,
        value: ""
      },
      imageMaxHeight: {
        type: String,
        value: 200,
        reflectToAttribute: true,
        observer: "__handleImageSizing"
      }
    };
  }

  static get observers() {
    return [
      '__handleImageSet(defaultImage, entityData.splices)'
    ]
  }


  /**
   * theres a bug with trying to make all the cards consistent height inside our horizontal-scroll class, we may need to use a getter to be able to style the div
   */
  get cardContentDiv() {
    if (!this._cardContentDiv) {
      this._cardContentDiv = this.root.querySelector('.card-content');
    }
    return this._cardContentDiv;
  }

  get card() {
    if (!this._card) {
      this._card = this.root.querySelector('#entityCard');
    }
    return this._card;
  }

  get descriptionDiv() {
    if (!this._descriptionDiv) {
      this._descriptionDiv = this.root.querySelector('.description')
    }
    return this._descriptionDiv;
  }

  __checkImage(loading) {
    if (loading) {
      return
    }

    requestAnimationFrame(() => {
      this.card.root.querySelector('iron-image').sizing = 'cover';
    });
  }


  __handleImageSet(defaultImage, splices) {
    if (!this.entityData) {
      return;
    }

    this.__displayDefault = !this.entityData.image && defaultImage
    this.__displayImage = !!this.entityData.image;
  }


  _setHeaderClickable() {
    if (!this.headerClickable) {
      return;
    }

    //TODO: find a better way to do this.
    requestAnimationFrame(() => {

      let imageHeader = this.root.querySelector('paper-card').root.querySelector('.header');
      imageHeader.style.cursor = "pointer";
      imageHeader.addEventListener('click', () => {
        let clickedEvent = new CustomEvent('image-link-clicked', {
          bubbles: true,
          composed: true,
          detail: {
            entity: this.entityData
          }
        });
        this.dispatchEvent(clickedEvent);
        if (this.defaultUrl) {
          window.location = this.defaultUrl;
        }
      });
    });
  }

  _handleEntityInformation(data) {
    if (this.interestedVersion === true) {
      this.$.captionDiv.hidden = false;
      this.$.captionDiv.style = "margin-top: -13%";
    }

    if(data.status && data.status !== "Accepted" && data.status !== "Rejected"){
      this.__displayStatus = true;
    }

    requestAnimationFrame(() => {
      if (data.description) {
        this.descriptionDiv.innerHTML = data.description;
      }

      if (this.__displayStatus) {
        let chip = this.root.querySelector('cbar-chip');
        chip.setAttribute('data-status', data.status);
      }
    });
  }

  __handleImageSizing(height) {

    requestAnimationFrame(() => {

      let images = this.root.querySelectorAll('.header-image');

      images.forEach((image) => {
        image.sizing = height && height > 0 ? "cover" : null;
        image.positioning = height && height > 0 ? "top" : null;
        image.style.height = height && height > 0 ? `${this.imageMaxHeight}px` : "auto";
      })
    })

  }

}

window.customElements.define(CbarEntityCard.is, CbarEntityCard);
